import { useRefMerged } from "@redotech/react-util/ref";
import { useRefSize } from "@redotech/react-util/size";
import { useTranslation } from "@redotech/redo-i18n";
import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import DotsHorizontalFilledIcon from "@redotech/redo-icons/dots-horizontal_filled.svg";
import classNames from "classnames";
import {
  Fragment,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Flex } from "../../flex";
import type { RedoListItem } from "../../list/redo-list";
import { RedoListDropdown } from "../../list/redo-list-dropdown";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { Tooltip } from "../../tooltip/tooltip";
import { NavIconSwap } from "../common/redo-nav-icon-swap";
import { NavEventTrackerContext } from "../nav-event-tracker-context";
import * as navigationCss from "../redo-navigation.module.css";
import { LinkPrimaryTab, PrimaryNavIconOnlyContext } from "./redo-primary-nav";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

const OVERFLOW_GAP_PX = 16; // --redo-spacing-xl
const DIVIDER_HEIGHT_PX = 1 + OVERFLOW_GAP_PX;

function dividerCount(
  visibleCount: number,
  sectionBoundaries: readonly number[],
): number {
  return sectionBoundaries.filter((b) => visibleCount > b).length;
}

/** Id under which the rendered More button's height is measured. */
const MORE_BUTTON_ID = "primary-nav-more-button";

/**
 * Decides how many of `itemIds` fit in `containerHeight`, summing each item's
 * measured height. Tabs differ in height (a label can wrap to two lines), so a
 * single representative height under-counts and clips the last tab.
 *
 * Items without a measurement are assumed to be as tall as the tallest measured
 * one. The More button uses its own measurement (`heights[MORE_BUTTON_ID]`)
 * once it has been rendered, and the first item's height until then. Returns
 * every item when nothing is measured yet.
 */
export function computeVisibleCount({
  itemIds,
  heights,
  containerHeight,
  sectionBoundaries,
}: {
  itemIds: readonly string[];
  heights: Readonly<Record<string, number>>;
  containerHeight: number;
  sectionBoundaries: readonly number[];
}): number {
  const total = itemIds.length;
  const measured = Object.entries(heights)
    .filter(([id, height]) => id !== MORE_BUTTON_ID && height > 0)
    .map(([, height]) => height);
  if (total === 0 || measured.length === 0) {
    return total;
  }
  const tallest = Math.max(...measured);
  const heightOf = (id: string) => heights[id] || tallest;

  const prefixHeights: number[] = [0];
  for (const id of itemIds) {
    prefixHeights.push((prefixHeights.at(-1) ?? 0) + heightOf(id));
  }

  const heightOfFirstCount = (count: number) =>
    (prefixHeights[count] ?? 0) +
    (count - 1) * OVERFLOW_GAP_PX +
    dividerCount(count, sectionBoundaries) * DIVIDER_HEIGHT_PX;

  if (heightOfFirstCount(total) <= containerHeight) {
    return total;
  }

  const firstId = itemIds[0];
  const moreHeight =
    heights[MORE_BUTTON_ID] ||
    (firstId !== undefined && heights[firstId]) ||
    tallest;
  for (let count = total - 1; count >= 1; count--) {
    if (
      heightOfFirstCount(count) + OVERFLOW_GAP_PX + moreHeight <=
      containerHeight
    ) {
      return count;
    }
  }
  return 1;
}

/**
 * Measures the primary-nav tab column and decides how many tabs fit, given the
 * available height. The remainder is meant to collapse into a "More" popover.
 * Recomputes when the container resizes, so it responds to window-height
 * changes.
 *
 * Attach `measureTab(id)` as the ref of each rendered item (its tab button),
 * and `measureTab(MORE_BUTTON_ID)` (via `measureMore`) to the More button, so
 * their real heights are used. Heights are kept while only the column's height
 * changes. Anything that can change a tab's own height (the nav width, label
 * text or visibility, the set of tabs) must be part of `layoutKey` or
 * `itemIds`: when either changes, every tab is rendered and measured again
 * before the visible count is recomputed, because tabs that overflowed out of
 * the DOM would otherwise keep a stale height.
 *
 * @param sectionBoundaries flat indices where a divider precedes a tab; used to
 *   reserve vertical space for the divider lines in the fit calculation.
 * @param layoutKey changes whenever a tab's rendered height may change.
 */
export function usePrimaryNavOverflow(
  itemIds: readonly string[],
  sectionBoundaries: readonly number[],
  layoutKey: string,
) {
  const [containerRef, containerSize] = useRefSize<HTMLElement>();

  const measureKey = `${layoutKey}|${itemIds.join(",")}`;
  const [measurement, setMeasurement] = useState<{
    key: string;
    heights: Record<string, number>;
  }>({ key: "", heights: {} });

  const elementsRef = useRef(new Map<string, HTMLElement>());
  const idByElementRef = useRef(new WeakMap<Element, string>());
  const observerRef = useRef<ResizeObserver | null>(null);
  const refCallbacksRef = useRef(
    new Map<string, (element: HTMLElement | null) => void>(),
  );

  const measureTab = useCallback((id: string) => {
    const existing = refCallbacksRef.current.get(id);
    if (existing) {
      return existing;
    }
    const callback = (element: HTMLElement | null) => {
      const previous = elementsRef.current.get(id);
      if (previous) {
        observerRef.current?.unobserve(previous);
        elementsRef.current.delete(id);
      }
      if (!element) {
        return;
      }
      if (!observerRef.current) {
        observerRef.current = new ResizeObserver((entries) => {
          const updates: Record<string, number> = {};
          for (const entry of entries) {
            const entryId = idByElementRef.current.get(entry.target);
            const entryElement =
              entryId === undefined
                ? undefined
                : elementsRef.current.get(entryId);
            if (entryId !== undefined && entryElement) {
              updates[entryId] = entryElement.getBoundingClientRect().height;
            }
          }
          setMeasurement((prev) => {
            const changed = Object.entries(updates).some(
              ([key, value]) => prev.heights[key] !== value,
            );
            return changed
              ? { key: prev.key, heights: { ...prev.heights, ...updates } }
              : prev;
          });
        });
      }
      elementsRef.current.set(id, element);
      idByElementRef.current.set(element, id);
      observerRef.current.observe(element);
    };
    refCallbacksRef.current.set(id, callback);
    return callback;
  }, []);

  const isMeasured = measurement.key === measureKey;

  // While the measurements are out of date every tab is rendered (see
  // visibleCount), so each one is in the DOM here and can be re-read before
  // paint.
  useLayoutEffect(() => {
    if (isMeasured) {
      return;
    }
    const heights: Record<string, number> = {};
    for (const [id, element] of elementsRef.current) {
      heights[id] = element.getBoundingClientRect().height;
    }
    setMeasurement({ key: measureKey, heights });
  }, [isMeasured, measureKey]);

  const visibleCount = useMemo(() => {
    if (!containerSize || !isMeasured) {
      return itemIds.length;
    }
    return computeVisibleCount({
      itemIds,
      heights: measurement.heights,
      containerHeight: containerSize.height,
      sectionBoundaries,
    });
  }, [
    containerSize,
    isMeasured,
    measurement.heights,
    itemIds,
    sectionBoundaries,
  ]);

  return {
    visibleCount,
    isOverflowing: visibleCount < itemIds.length,
    containerRef,
    measureTab,
    measureMore: measureTab(MORE_BUTTON_ID),
  };
}

export function tabToListItem(
  tab: LinkPrimaryTab,
): RedoListItem<LinkPrimaryTab> {
  return {
    id: tab.id,
    type: "text" as const,
    text: tab.friendlyName,
    leadingItem: { type: "icon" as const, Icon: tab.UnfilledIcon },
    value: tab,
  };
}

export const PrimaryNavOverflowPopover = memo(
  function PrimaryNavOverflowPopover({
    overflowTabs,
    addSectionTabs = [],
    selectedTabId,
    measureRef,
  }: {
    overflowTabs: LinkPrimaryTab[];
    /**
     * When the Add button itself has overflowed, the available products it
     * would have contained are inlined into the More menu under an "Add"
     * subheader instead of appearing as a single "Add" entry.
     */
    addSectionTabs?: LinkPrimaryTab[];
    selectedTabId: string | undefined;
    /** Ref callback that measures the More button's height. */
    measureRef?: (element: HTMLElement | null) => void;
  }) {
    const trackNavEvent = useContext(NavEventTrackerContext);
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const iconOnly = useContext(PrimaryNavIconOnlyContext);
    const navigate = useNavigate();

    const hasSelectedOverflow = overflowTabs.some(
      (tab) => tab.id === selectedTabId,
    );

    const handleOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const listItems: RedoListItem<LinkPrimaryTab>[] = useMemo(() => {
      const items: RedoListItem<LinkPrimaryTab>[] =
        overflowTabs.map(tabToListItem);
      if (addSectionTabs.length > 0) {
        items.push({
          id: "add-section-header",
          type: "custom" as const,
          render: (
            <Text fontSize="xs" textColor="tertiary">
              {t("Add", { context: "navigation menu" })}
            </Text>
          ),
          useWrapper: false,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          value: null as unknown as LinkPrimaryTab,
        });
        for (const tab of addSectionTabs) {
          items.push(tabToListItem(tab));
        }
      }
      return items;
    }, [overflowTabs, addSectionTabs, t]);

    const handleItemSelected = useCallback(
      ({ item }: { item: RedoListItem<LinkPrimaryTab> }) => {
        const tab = item.value;
        if (!tab) {
          return;
        }
        trackNavEvent("nav/sidebar-item-clicked", {
          section: tab.id,
          item: tab.id,
        });
        navigate(tab.href);
      },
      [navigate, trackNavEvent],
    );

    const buttonRef = useRefMerged<HTMLElement>(
      measureRef ? [setAnchorEl, measureRef] : [setAnchorEl],
    );

    const moreButton = (
      <Flex
        align="center"
        as="button"
        className={classNames(
          navigationCss.primaryTab,
          hasSelectedOverflow && navigationCss.selected,
        )}
        data-testid="primary-nav-more"
        dir="column"
        gap="xxs"
        justify="center"
        onClick={handleOpen}
        ref={buttonRef}
      >
        <Flex
          align="center"
          className={navigationCss.primaryTabIconBox}
          justify="center"
        >
          <NavIconSwap
            FilledIcon={DotsHorizontalFilledIcon}
            isSelected={hasSelectedOverflow}
            size={20}
            UnfilledIcon={DotsHorizontalIcon}
          />
        </Flex>
        {!iconOnly && (
          <Text
            as="span"
            fontSize="xxs"
            textColor={hasSelectedOverflow ? "primary" : "secondary"}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {t("More", { context: "navigation menu" })}
          </Text>
        )}
      </Flex>
    );

    return (
      <>
        {iconOnly ? (
          <Tooltip
            darkMode
            placement="right"
            title={t("More", { context: "navigation menu" })}
          >
            {moreButton}
          </Tooltip>
        ) : (
          moreButton
        )}
        <RedoListDropdown
          darkMode
          dropdownAnchor={anchorEl}
          dropdownOpen={open}
          dropdownPlacement="right-start"
          items={listItems}
          itemSelected={handleItemSelected}
          selectedItems={selectedTabId}
          selectionVariant="none"
          setDropdownOpen={setOpen}
          size="xs"
        />
      </>
    );
  },
);

export interface PrimaryNavGroup {
  id: string;
  tabs: LinkPrimaryTab[];
}

/**
 * General primary-nav column: a vertical icon rail of tab groups (separated by
 * dividers) that collapses overflowing tabs into a "More" popover when the
 * viewport is too short to show them all. Optional `header` and `footer` slots
 * render above the scrolling tab list and pinned at the bottom, respectively.
 *
 * The overflow math applies only to `groups`; `header`/`footer` are always
 * visible. Used by the admin nav and available to any nav that needs the same
 * responsive primary rail.
 */
export const RedoPrimaryNavRail = memo(function RedoPrimaryNavRail({
  groups,
  selectedTabId,
  header,
  footer,
  iconOnly = false,
  width = 72,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  groups: PrimaryNavGroup[];
  selectedTabId?: string;
  header?: ReactNode;
  footer?: ReactNode;
  iconOnly?: boolean;
  width?: number;
}) {
  const flatTabs = useMemo(
    () => groups.flatMap((group) => group.tabs),
    [groups],
  );

  const sectionBoundaries = useMemo(() => {
    const boundaries: number[] = [];
    let acc = 0;
    for (let i = 0; i < groups.length - 1; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc += groups[i]!.tabs.length;
      if (acc > 0) {
        boundaries.push(acc);
      }
    }
    return boundaries;
  }, [groups]);

  const boundarySet = useMemo(
    () => new Set(sectionBoundaries),
    [sectionBoundaries],
  );

  const tabIds = useMemo(() => flatTabs.map((tab) => tab.id), [flatTabs]);

  const layoutKey = `${width}|${iconOnly}|${flatTabs
    .map((tab) => `${tab.friendlyName}${tab.hideLabel ? "-hidden" : ""}`)
    .join(",")}`;

  const { visibleCount, isOverflowing, containerRef, measureTab, measureMore } =
    usePrimaryNavOverflow(tabIds, sectionBoundaries, layoutKey);

  const visibleTabs = flatTabs.slice(0, visibleCount);
  const overflowTabs = flatTabs.slice(visibleCount);

  return (
    <PrimaryNavIconOnlyContext.Provider value={iconOnly}>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(darkModeCss.darkMode, navigationCss.primaryNav)}
        data-arbiter="RedoPrimaryNavRail"
        data-dd-action-name={actionName}
        dir="column"
        grow={1}
        justify="space-between"
        px="xs"
        py="xl"
        style={{ width, maxWidth: width }}
      >
        <Flex
          align="center"
          className={navigationCss.primaryNavTabSection}
          dir="column"
          gap="xl"
          grow={1}
          shrink={1}
        >
          {header}
          <Flex
            align="center"
            className={navigationCss.primaryNavTabSection}
            dir="column"
            gap="xl"
            grow={1}
            ref={containerRef}
            shrink={1}
          >
            {visibleTabs.map((tab, i) => (
              <Fragment key={tab.id}>
                {boundarySet.has(i) && (
                  <Flex className={navigationCss.primaryNavDivider} />
                )}
                <Flex dir="column" ref={measureTab(tab.id)} w="full">
                  <PrimaryNavigationTab
                    selectedTabId={selectedTabId}
                    tab={tab}
                  />
                </Flex>
              </Fragment>
            ))}
            {isOverflowing && (
              <PrimaryNavOverflowPopover
                measureRef={measureMore}
                overflowTabs={overflowTabs}
                selectedTabId={selectedTabId}
              />
            )}
          </Flex>
        </Flex>
        {footer && (
          <Flex align="center" dir="column" gap="xl" shrink={0}>
            {footer}
          </Flex>
        )}
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});
